import type { DocxIntegrityIssue, DocxIntegrityRepair } from '@hector/shared-types';
import JSZip from 'jszip';
import { ArchiveFault, inspectZipDirectory, isXmlPart, verifyZipPayloads, type XmlLimitEvidence } from './zipDirectory';
import { repairInvalidParagraphJustification } from './justificationRepair';
import { repairRunsOutsideParagraph } from './runOutsideParagraphRepair';
import { repairTrackedLinks } from './trackedLinkRepair';
import { repairOrphanTable, type TableRecoveryProof } from './tableRepair';
import { repairOrphanRelationships, resolveTarget } from './orphanRelationshipsRepair';
import { decodeXml, descendants, isWord, PACKAGE_REL_NS, parseXml, REL_NS, STRICT_REL_NS, type XmlNode, XmlFault } from './xml';

export type { DocxIntegrityIssue, DocxIntegrityRepair } from '@hector/shared-types';
export type { TableRecoveryProof } from './tableRepair';
// The strict, namespace-aware XML reader is shared with consumers that read a
// DOCX part's content (Canvas form reader, #1859): one parser, same DTD refusal.
export { parseXml, isWord, XmlFault, type XmlNode } from './xml';

/** A refusal, with bounded evidence and the lawyer-facing next step. */
type RejectedDocx = { status: 'rejected'; issues: DocxIntegrityIssue[] };

export type PreparedDocx =
  | { status: 'valid' | 'repaired'; bytes: Uint8Array; repairs: DocxIntegrityRepair[] }
  | RejectedDocx;

const CONTENT_TYPES_NS = 'http://schemas.openxmlformats.org/package/2006/content-types';

/** Byte-preserving repairs of a `word/*.xml` part, applied in order before it is parsed. */
const WORD_PART_REPAIRS = [
  ['INVALID_PARAGRAPH_JUSTIFICATION', repairInvalidParagraphJustification],
  ['RUN_OUTSIDE_PARAGRAPH', repairRunsOutsideParagraph],
] as const;

/** Optional bounded evidence of a refusal; an object so no field can shift into another. */
interface RejectionDetail {
  readonly part?: string;
  readonly location?: string;
  readonly xmlLimit?: XmlLimitEvidence;
}

const sizeFormat = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });
const mo = (bytes: number): string => `${sizeFormat.format(bytes / (1024 * 1024))} Mo`;

/** Lawyer-facing next step per issue code; a function when the text needs the refusal's evidence. */
const actions: Record<string, string | ((detail: RejectionDetail) => string)> = {
  DOCX_TRACKED_LINK_UNSUPPORTED: 'Relire les suppressions suivies autour du lien dans cette partie. La correction automatique ne peut pas conserver leur portée avec certitude. Ne pas rejouer la modification déjà appliquée.',
  DOCX_ORPHAN_TABLE_CLOSURES: 'Relire les tableaux et les révisions récemment acceptées. Des fins de tableau sans début subsistent dans le fichier ; une preuve du modèle vivant est nécessaire avant correction.',
  DOCX_RELATIONSHIP_INVALID: 'Une référence interne du document ne désigne aucun élément disponible. Relire le lien ou le média concerné avant un nouvel export.',
  DOCX_ARCHIVE_LIMIT: 'Le document est trop volumineux pour être vérifié : son archive dépasse les plafonds de contrôle (taille, nombre de parties ou taille dépliée). Scinder ou alléger le document, puis réessayer.',
  // #1744: sizes, not content. « au moins » because counting stops at the ceiling.
  DOCX_XML_LIMIT: ({ xmlLimit }) => (xmlLimit
    ? `Le document contient au moins ${mo(xmlLimit.totalBytes)} de XML déplié, au-delà du plafond de ${mo(xmlLimit.capBytes)} `
      + `(partie la plus lourde : ${xmlLimit.largestPart.slice(0, 120)}, ${mo(xmlLimit.largestBytes)}). `
    : '')
    + 'Un document de 2 000 pages de texte en contient environ 15 Mo : ce volume signale un contenu anormal, pas un long document. '
    + 'Il est refusé avant toute analyse. Scinder ou alléger le document, puis réessayer.',
};

function rejected(code: string, detail: RejectionDetail = {}): RejectedDocx {
  const action = actions[code];
  return { status: 'rejected', issues: [{ code,
    ...(detail.part ? { part: detail.part.slice(0, 240) } : {}),
    ...(detail.location ? { location: detail.location.slice(0, 120) } : {}),
    action: (typeof action === 'function' ? action(detail) : action)
      ?? 'Le fichier exporté ne respecte pas la structure DOCX attendue. Conserver le document actuel et relire la dernière modification avant de réessayer ; ne pas supprimer du contenu pour contourner ce contrôle.' }] };
}

/** Refusal of an archive-level fault; anything but an `ArchiveFault` is an unreadable archive. */
function archiveRejection(error: unknown): RejectedDocx {
  return error instanceof ArchiveFault
    ? rejected(error.code, { part: error.xmlLimit?.largestPart, xmlLimit: error.xmlLimit })
    : rejected('DOCX_ARCHIVE_INVALID');
}

/** Outcome of {@link checkDocxArchiveBounds}. */
export type DocxArchiveBoundsCheck = { status: 'within_bounds' } | RejectedDocx;

/**
 * The archive gate of `prepareDocx`, alone: directory inspection, then bounded
 * inflation of every entry against the size ceilings (`DOCX_ARCHIVE_LIMIT`,
 * `DOCX_XML_LIMIT`), with actual lengths and CRCs checked. For a server caller
 * that must open an untrusted DOCX with JSZip in its own process and keep the
 * archive (#1761, template-adapt `DocxParser`): past this check, JSZip's
 * expansion is bounded by the same ceilings. It does not validate the package
 * (parts, XML, relationships): only `prepareDocx` does.
 *
 * Costs one bounded inflation pass (16 Kio slices, nothing retained).
 *
 * @param bytes - The DOCX archive
 * @returns `within_bounds`, or the refusal with its lawyer-facing action
 */
export function checkDocxArchiveBounds(bytes: Uint8Array): DocxArchiveBoundsCheck {
  try {
    verifyZipPayloads(bytes, inspectZipDirectory(bytes));
    return { status: 'within_bounds' };
  } catch (error) {
    return archiveRejection(error);
  }
}

function hasNestedTrackedLink(root: XmlNode): boolean {
  for (const node of descendants(root)) {
    if (isWord(node, 'del') && [...descendants(node)].some((child) => isWord(child, 'hyperlink'))) return true;
  }
  return false;
}

function validatePackage(parts: Map<string, XmlNode>, names: Set<string>): { code: string; part: string } | null {
  const document = parts.get('word/document.xml');
  if (!document || !isWord(document, 'document') || document.children.filter((node) => isWord(node, 'body')).length !== 1) {
    return { code: 'DOCX_MAIN_PART_INVALID', part: 'word/document.xml' };
  }
  const contentTypes = parts.get('[Content_Types].xml');
  if (!contentTypes || contentTypes.uri !== CONTENT_TYPES_NS || contentTypes.local !== 'Types') {
    return { code: 'DOCX_CONTENT_TYPES_INVALID', part: '[Content_Types].xml' };
  }
  const mainType = contentTypes.children.find((node) => node.local === 'Override'
    && node.uri === CONTENT_TYPES_NS && node.attributes['{}PartName'] === '/word/document.xml')?.attributes['{}ContentType'];
  if (mainType !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml') {
    return { code: 'DOCX_CONTENT_TYPES_INVALID', part: '[Content_Types].xml' };
  }
  const relations = new Map<string, Set<string>>();
  let hasMainRelationship = false;
  for (const [part, root] of parts) {
    if (!part.endsWith('.rels')) continue;
    const match = /^(.*\/)?_rels\/([^/]*)\.rels$/.exec(part);
    const source = part === '_rels/.rels' ? '' : match ? `${match[1] ?? ''}${match[2] ?? ''}` : null;
    // SuperDoc keeps an empty footnotes.xml.rels when no footnote part exists.
    // It references nothing and Word opens it; blocking it would reject healthy
    // exports. A nonempty relationship set still requires its owning part.
    if (source === null || root.uri !== PACKAGE_REL_NS || root.local !== 'Relationships' || (source && !names.has(source) && root.children.length > 0)) {
      return { code: 'DOCX_RELATIONSHIP_INVALID', part };
    }
    const ids = new Set<string>();
    for (const relation of root.children) {
      const id = relation.attributes['{}Id'];
      const target = relation.attributes['{}Target'];
      const type = relation.attributes['{}Type'];
      const mode = relation.attributes['{}TargetMode'];
      if (relation.uri !== PACKAGE_REL_NS || relation.local !== 'Relationship' || !id || !target || !type || ids.has(id)
        || (mode !== undefined && mode !== 'Internal' && mode !== 'External')) return { code: 'DOCX_RELATIONSHIP_INVALID', part };
      ids.add(id);
      if (mode === 'External') continue;
      const resolved = resolveTarget(source, target);
      if (!resolved || !names.has(resolved) || resolved.endsWith('/')) return { code: 'DOCX_RELATIONSHIP_INVALID', part };
      if (source === '' && (type === `${REL_NS}/officeDocument` || type === `${STRICT_REL_NS}/officeDocument`) && resolved === 'word/document.xml') hasMainRelationship = true;
    }
    relations.set(source, ids);
  }
  if (!hasMainRelationship) return { code: 'DOCX_RELATIONSHIP_INVALID', part: '_rels/.rels' };
  for (const [part, root] of parts) {
    for (const node of descendants(root)) {
      for (const [name, value] of Object.entries(node.attributes)) {
        if ((name.startsWith(`{${REL_NS}}`) || name.startsWith(`{${STRICT_REL_NS}}`)) && !relations.get(part)?.has(value)) {
          return { code: 'DOCX_RELATIONSHIP_INVALID', part };
        }
      }
    }
  }
  return null;
}

/**
 * Validate the actual exported package, repairing only evidenced SuperDoc
 * serialization faults. This does not certify layout or complete OOXML schema
 * conformance. A healthy archive is returned byte-for-byte without rewriting.
 */
export async function prepareDocx(bytes: Uint8Array, options: { tableRecoveryProof?: TableRecoveryProof } = {}): Promise<PreparedDocx> {
  let archive: JSZip; let names: Set<string>;
  try {
    const inspection = inspectZipDirectory(bytes);
    names = inspection.names;
    for (const required of ['[Content_Types].xml', '_rels/.rels', 'word/document.xml']) {
      if (!names.has(required)) return rejected('DOCX_PART_MISSING', { part: required });
    }
    // The directory's expanded-size fields are not trusted: verifyZipPayloads
    // feeds the inflater bounded compressed slices and counts each step's actual
    // output, aborting mid-entry past a cap, before JSZip's CRC pass may expand
    // the package.
    verifyZipPayloads(bytes, inspection);
    archive = await JSZip.loadAsync(bytes, { checkCRC32: true, createFolders: false });
    for (const entry of Object.values(archive.files)) {
      if (entry.unsafeOriginalName && entry.unsafeOriginalName !== entry.name) return rejected('DOCX_ARCHIVE_INVALID');
    }
  } catch (error) {
    return archiveRejection(error);
  }
  const parts = new Map<string, XmlNode>();
  const repairs: DocxIntegrityRepair[] = [];
  // Before the parts are read: a removed entry must be neither parsed nor validated.
  for (const orphan of await repairOrphanRelationships(archive, names)) {
    repairs.push({ code: 'ORPHAN_PART_RELATIONSHIPS', ...orphan });
  }
  for (const [part, entry] of Object.entries(archive.files)) {
    if (entry.dir || !isXmlPart(part)) continue;
    let xml: string;
    try { xml = decodeXml(await entry.async('uint8array')); }
    catch { return rejected('DOCX_XML_ENCODING_INVALID', { part }); }
    if (part.startsWith('word/') && part.endsWith('.xml')) {
      const original = xml;
      for (const [code, repair] of WORD_PART_REPAIRS) {
        const repaired = repair(xml);
        if (repaired.count === 0) continue;
        xml = repaired.xml;
        repairs.push({ code, part, count: repaired.count });
      }
      if (xml !== original) {
        archive.file(part, xml.replace(/^(<\?xml\s[^?]*encoding\s*=\s*["'])[^"']+(["'])/, '$1UTF-8$2'), {
          date: entry.date, comment: entry.comment, createFolders: false,
          unixPermissions: entry.unixPermissions, dosPermissions: entry.dosPermissions,
        });
      }
    }
    let parsed: XmlNode | undefined;
    let fault: unknown;
    try { parsed = parseXml(xml); } catch (error) { fault = error; }
    if (!parsed && part === 'word/document.xml' && options.tableRecoveryProof) {
      const repaired = repairOrphanTable(xml, options.tableRecoveryProof);
      if (repaired !== null) {
        parsed = parseXml(repaired);
        archive.file(part, repaired.replace(/^(<\?xml\s[^?]*encoding\s*=\s*["'])[^"']+(["'])/, '$1UTF-8$2'), {
          date: entry.date, comment: entry.comment, createFolders: false,
          unixPermissions: entry.unixPermissions, dosPermissions: entry.dosPermissions,
        });
        repairs.push({ code: 'ORPHAN_TABLE_CLOSURES_WITH_LIVE_PROOF', part, count: 1 });
      }
    }
    if (!parsed || hasNestedTrackedLink(parsed)) {
      if (fault instanceof XmlFault && fault.code === 'DOCX_DTD_FORBIDDEN') return rejected(fault.code, { part });
      if (!xml.includes('<w:del') || !xml.includes('<w:hyperlink')) {
        return rejected(/<\/w:tc>\s*<\/w:tr>\s*<\/w:tbl>/.test(xml) && !xml.includes('<w:tbl') ? 'DOCX_ORPHAN_TABLE_CLOSURES'
          : fault instanceof XmlFault ? fault.code : 'DOCX_XML_INVALID', { part, location: fault instanceof XmlFault ? fault.location : undefined });
      }
      try {
        const repaired = repairTrackedLinks(xml);
        if (!repaired.count || repaired.xml === xml) return rejected('DOCX_TRACKED_LINK_UNSUPPORTED', { part });
        parsed = parseXml(repaired.xml);
        if (hasNestedTrackedLink(parsed)) return rejected('DOCX_TRACKED_LINK_UNSUPPORTED', { part });
        // Edits preserve all character/markup intervals except deletion spans.
        // Re-encode to UTF-8 with a matching declaration when an input used UTF-16.
        const output = repaired.xml.replace(/^(<\?xml\s[^?]*encoding\s*=\s*["'])[^"']+(["'])/, '$1UTF-8$2');
        // Preserve entry metadata as well as contents: otherwise JSZip stamps
        // the current time and a retry of one attempt has a different hash.
        archive.file(part, output, {
          date: entry.date, comment: entry.comment, createFolders: false,
          unixPermissions: entry.unixPermissions, dosPermissions: entry.dosPermissions,
        });
        repairs.push({ code: 'TRACKED_HYPERLINK_SCOPE', part, count: repaired.count });
      } catch (error) {
        return rejected(error instanceof XmlFault ? error.code : 'DOCX_TRACKED_LINK_UNSUPPORTED', { part, location: error instanceof XmlFault ? error.location : undefined });
      }
    }
    parts.set(part, parsed);
  }
  const failure = validatePackage(parts, names);
  if (failure) return rejected(failure.code, { part: failure.part });
  if (!repairs.length) return { status: 'valid', bytes, repairs };
  const repairedBytes = await archive.generateAsync({ type: 'uint8array', compression: 'DEFLATE' });
  // A repair must be a fixed point of the same gate, not a special bypass.
  const verified = await prepareDocx(repairedBytes);
  if (verified.status !== 'valid') return rejected('DOCX_REPAIR_NOT_STABLE');
  return { status: 'repaired', bytes: repairedBytes, repairs };
}
